import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';

import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';

import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { ConfiguracaoFinanceiraGeralService } from '../../../../shared/services/apis/financas/configuracao-financeira-geral.service';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';

import { TipoEstornoConta } from '../../../../shared/services/models/financas/tipo-estorno-conta.model';
import { FormaRecebimento } from '../../../../shared/services/models/financas/forma-recebimento.model';
import { TipoDataRecebimentoPadrao } from '../../../../shared/services/models/financas/tipo-data-recebimento-padrao.model';
import { TipoBaixaTitulo } from '../../../../shared/services/models/financas/tipo-baixa-titulo.model';
import { TipoCategoriaRetornoBancario } from '../../../../shared/services/models/financas/tipo-categoria-retorno-bancario.model';
import { CategoriaFinanceira } from '../../../../shared/services/models/financas/categoria-financeira.model';
import { FormaPagamento } from '../../../../shared/services/models/financas/forma-pagamento.model';
import { ConfiguracaoFinanceiraGeralRequest } from '../../../../shared/services/requests/financas/configuracao-financeira-geral.request';
import { TipoBaixaTituloEnum } from '../../../../shared/enums/financas/tipo-baixa-titulo.enum';
import { TipoEstornoContaEnum } from '../../../../shared/enums/financas/tipo-estorno-conta.enum';
import { TipoDataRecebimentoPadraoEnum } from '../../../../shared/enums/financas/tipo-data-recebimento-padrao.enum';
import { TipoCategoriaRetornoBancarioEnum } from '../../../../shared/enums/financas/tipo-categoria-retorno-bancario.enum';
import { ConfiguracaoFinanceiraGeralLookupsResponse } from '../../../../shared/services/responses/financas/configuracao-financeira-geral-lookups.response';
import { ConfiguracaoFinanceiraGeralResponse } from '../../../../shared/services/responses/financas/configuracao-financeira-geral.response';

@Component({
  selector: 'app-config-geral-financas',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
  ],
  templateUrl: './config-geral-financas.component.html',
  styleUrls: ['./config-geral-financas.component.css'],
})
export class ConfigGeralFinancasComponent implements OnInit {
  form: FormGroup;
  isLoading = false;
  isSaving = false;
  funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Financas;
  moduloEnum = ModuloEnum.Configuracoes;

  opcoesTipoEstornoConta: TipoEstornoConta[] = [];
  opcoesFormaPagamento: FormaPagamento[] = [];
  opcoesFormaRecebimento: FormaRecebimento[] = [];
  opcoesTipoDataRecebimentoPadrao: TipoDataRecebimentoPadrao[] = [];
  opcoesTipoBaixaTitulo: TipoBaixaTitulo[] = [];
  opcoesTipoCategoriaRetornoBancario: TipoCategoriaRetornoBancario[] = [];
  opcoesCategoriaFinanceira: CategoriaFinanceira[] = [];

  tipoBaixaTituloEnum = TipoBaixaTituloEnum;
  tipoCategoriaRetornoBancarioEnum = TipoCategoriaRetornoBancarioEnum;

  constructor(
    private formBuilder: FormBuilder,
    private configGeralService: ConfiguracaoFinanceiraGeralService,
    private alertService: AlertService,
    private router: Router
  ) {
    this.form = this.formBuilder.group({
      idTipoEstornoConta: [TipoEstornoContaEnum.AutomaticoAoCancelarPedido, Validators.required],
      idFormaPagamento: [null],
      idFormaRecebimento: [null],
      idTipoDataRecebimentoPadrao: [TipoDataRecebimentoPadraoEnum.Pagamento, Validators.required],
      idTipoBaixaTitulo: [TipoBaixaTituloEnum.Agrupado, Validators.required],
      idTipoCategoriaRetornoBancario: [null],
      idCategoriaFinanceiraLiquidacao: [null],
      isExibirHistoricoLogo: [false],
      isEnviarLinhaDigitavel: [{ value: false, disabled: true }],
      isAtualizarVencimentoNf: [false],
      isLancarTarifasCaixa: [false],
      idCategoriaFinanceiraTarifa: [null],
      isMarcarCanceladoBaixaBanco: [false],
    });
  }

  ngOnInit(): void {
    this.loadLookups();
    this.setupListeners();
  }

  setupListeners(): void {
    this.form.get('idTipoBaixaTitulo')?.valueChanges.subscribe(value => {
      if (value === TipoBaixaTituloEnum.Individual) {
        this.form.get('idTipoCategoriaRetornoBancario')?.setValidators([Validators.required]);
      } else if (value === TipoBaixaTituloEnum.Agrupado) {
        this.form.get('idCategoriaFinanceiraLiquidacao')?.setValidators([Validators.required]);
      }
      this.form.get('idTipoCategoriaRetornoBancario')?.clearValidators();
      this.form.get('idTipoCategoriaRetornoBancario')?.setValue(null);
      this.form.get('idTipoCategoriaRetornoBancario')?.updateValueAndValidity();

      this.form.get('idCategoriaFinanceiraLiquidacao')?.clearValidators();
      this.form.get('idCategoriaFinanceiraLiquidacao')?.setValue(null);
      this.form.get('idCategoriaFinanceiraLiquidacao')?.updateValueAndValidity();
    });

    this.form.get('idTipoCategoriaRetornoBancario')?.valueChanges.subscribe(value => {
      if (this.form.get('idTipoBaixaTitulo')?.value === TipoBaixaTituloEnum.Individual) {
        if (value === TipoCategoriaRetornoBancarioEnum.UsarUmaCategoriaEspecifica) {
          this.form.get('idCategoriaFinanceiraLiquidacao')?.setValidators([Validators.required]);
        } else {
          this.form.get('idCategoriaFinanceiraLiquidacao')?.clearValidators();
          this.form.get('idCategoriaFinanceiraLiquidacao')?.setValue(null);
        }
        this.form.get('idCategoriaFinanceiraLiquidacao')?.updateValueAndValidity();
      }
    });

    this.form.get('isLancarTarifasCaixa')?.valueChanges.subscribe(value => {
      if (value) {
        this.form.get('idCategoriaFinanceiraTarifa')?.setValidators([Validators.required]);
      } else {
        this.form.get('idCategoriaFinanceiraTarifa')?.clearValidators();
        this.form.get('idCategoriaFinanceiraTarifa')?.setValue(null);
      }
      this.form.get('idCategoriaFinanceiraTarifa')?.updateValueAndValidity();
    });
  }

  loadLookups(): void {
    this.isLoading = true;
    this.configGeralService.getLookups().subscribe({
      next: (response: ConfiguracaoFinanceiraGeralLookupsResponse) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }

        this.opcoesTipoEstornoConta = response.opcoesTipoEstornoConta || [];
        this.opcoesFormaPagamento = response.opcoesFormaPagamento || [];
        this.opcoesFormaRecebimento = response.opcoesFormaRecebimento || [];
        this.opcoesTipoDataRecebimentoPadrao = response.opcoesTipoDataRecebimentoPadrao || [];
        this.opcoesTipoBaixaTitulo = response.opcoesTipoBaixaTitulo || [];
        this.opcoesTipoCategoriaRetornoBancario = response.opcoesTipoCategoriaRetornoBancario || [];
        this.opcoesCategoriaFinanceira = response.opcoesCategoriaFinanceira || [];
        this.loadConfigData();
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  loadConfigData(): void {
    this.configGeralService.getConfiguracao().subscribe({
      next: (response: ConfiguracaoFinanceiraGeralResponse) => {
        this.isLoading = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.form.patchValue(response.configuracaoFinanceiraGeral);
        this.onExibirHistoricoLogoChange({
          checked: response.configuracaoFinanceiraGeral.isExibirHistoricoLogo,
        });
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  salvar(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.alertService.show('Atenção', 'Preencha os campos obrigatórios.', AlertType.error);
      return;
    }

    this.isSaving = true;
    const request: ConfiguracaoFinanceiraGeralRequest = this.form.getRawValue();

    this.configGeralService.updateConfiguracao(request).subscribe({
      next: (response: any) => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.alertService.show('Sucesso', 'Configurações salvas com sucesso!', AlertType.success);
        this.voltar();
      },
      error: (error: any) => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  voltar(): void {
    this.router.navigate(['/configuracoes/financas']);
  }

  onExibirHistoricoLogoChange(event: any): void {
    if (event.checked) {
      this.form.get('isEnviarLinhaDigitavel')?.enable();
    } else {
      this.form.get('isEnviarLinhaDigitavel')?.disable();
      this.form.get('isEnviarLinhaDigitavel')?.setValue(false);
      this.form.get('isEnviarLinhaDigitavel')?.updateValueAndValidity();
    }
  }
}
